import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUsuario } from '../../context/usuarioContext';
// Componentes UI
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { classNames } from 'primereact/utils';
import { Tag } from 'primereact/tag';
// Componentes
import Permiso from '../../components/wrappers/permiso';
import TableLayout from "../../layouts/layout-tabla";
// Datos
import { obtenerCklSomnolencias, obtenerCklSomnolencia } from '../../services/conductoresService.js';
// Utils
import { exportarExcel } from '../../utils/exportarExcel.js';
import { exportarPDF } from '../../utils/pdf/pdfDataTable.js';
import { generarPDF } from "../../utils/pdf/pdfCklSomnolencia.js";
import { formatDateTimeFront, inicioDelDia, finDelDia } from '../../utils/dateFormatter.js';
// Estilos y Assets
import logo from "../../assets/logos/TRANSKYRIOS-_IconColor.avif";
import useMedia from '../../hooks/media-querys.js';
import '../../pages/panel-prevencion/panel-prevencion.css';

function HistChkSomnolencia() {
  const isPhone  = useMedia('(max-width: 640px)');
  const labelPdf   = isPhone ? 'PDF'   : 'Exportar PDF';
  const labelExcel = isPhone ? 'Excel' : 'Exportar Excel';
  const labelBuscar = isPhone ? '' : 'Buscar';
  const fixWidth = isPhone ? "80px" : "auto";
  const fixSize = isPhone ? "0.75rem" : "1rem";
  
  const { tienePermiso } = useUsuario();
  const puedeVerDet = tienePermiso('Panel Prevención', 'PRE_VER_DETSOM');
  const toast = useRef(null);
  const dt = useRef(null);
  const navigate = useNavigate();
  
  // Filtro por rango de fechas
  const [dates, setDates] = useState(null);

  // Obtener informes somnolencia defecto, primeros 100
  const [informes, setInformes] = useState([]);
  useEffect(() => { obtenerRegistros() }, []);

  // Barra filtro global
  const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
  const [filteredvalue, setFilteredvalue] = useState([]);
  const [globalFilterValue, setGlobalFilterValue] = useState('');

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;

    let _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);

    // Registros filtrados
    const filtrado = informes.filter((item) => {
      return Object.values(item).some((val) =>
        String(val).toLowerCase().includes(value.toLowerCase())
      );
    });
    setFilteredvalue(filtrado);
  };

  // Utilidad: detectar iOS/iPadOS (incluye iPad con userAgent de Mac)
  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  // Ver registro con detalle (abrir PDF)
  const verRegistro = (id_checklist) => {
    // 1) Pre-abrir la ventana para no perder el gesto del usuario
    const win = window.open('', '_blank');
    if (!win) {
      toast.current.show({
        severity: 'warn',
        summary: 'Pop-up bloqueado',
        detail: 'Habilita las ventanas emergentes para ver el PDF.',
        life: 4000
      });
      return;
    }

    (async () => {
      try {
        const datos = await obtenerCklSomnolencia(id_checklist);
        if (!datos) throw new Error('No se recibieron datos');
        const pdf = generarPDF(datos);

        if (isIOS()) {
          // 2) iOS: usar Blob + object URL
          pdf.getBlob((blob) => {
            const url = URL.createObjectURL(blob);
            win.location.href = url; // asignar a la ventana ya abierta
            win.onbeforeunload = () => URL.revokeObjectURL(url);
          });
        } else {
          // 3) Otros navegadores: abrir dentro de la misma ventana pre-abierta
          pdf.open({}, win);
        }
      } catch (error) {
        // cerrar la ventana pre-abierta si algo falla
        try { win.close(); } catch {}
        toast.current.show({ severity: 'error', summary: 'Error', life: 3000,
          detail: `Error al cargar el informe: ${id_checklist || 'desconocido'}.`
        });
        console.error('Error cargando informe:', error);
      }
    })();
  };

  // Obtener registros desde el servicio
  const obtenerRegistros = (desde, hasta) => {
    obtenerCklSomnolencias(desde, hasta)
      .then(data => {
        const formatData = data.map(item => ({
          ...item,
          creado_en: formatDateTimeFront(item.creado_en),
        }));
        setInformes(formatData);
      })
      .catch(error => {
        toast.current.show({ severity: 'error', summary: 'Error', life: 3000,
          detail: 'No se pudieron cargar los informes.'
        });
      });
  };

  // Buscar registros por rango de fechas
  const buscarRegistros = () => {
    if (!dates || dates.length !== 2) {
      toast.current.show({ severity: 'info', summary: 'Seleccione un periodo', life: 4000, detail: 'Ingrese un periodo para consultar registros.' });
      obtenerRegistros();
      return;
    }
    const [desdeDate, hastaDate] = dates;
    const desde = inicioDelDia(desdeDate); // YYYY-MM-DD 00:00:00
    const hasta = finDelDia(hastaDate); // YYYY-MM-DD 23:59:59
    obtenerRegistros(desde, hasta);
  };

  // Templates Columnas
    // Acción
  const accionTemplate = (rowData) => (
    <div className="accion-container">
      <Button 
        className="btn-editar" 
        icon="pi pi-eye"
        tooltip="Ver detalle"
        tooltipOptions={{ position: 'top' }}
        onClick={() => verRegistro(rowData.id_checklist)}
        disabled={!puedeVerDet}
      />
    </div>
  );
    // Cantidad de Síntomas
  const sintomasTemplate = (rowData) => {
    const sintomasClassName = classNames('alertas-badge', { 'alerta-roja': rowData.cantSintomas >= 1, });
    return <div className={sintomasClassName}>{rowData.cantSintomas}</div>;
  };

  // Conductor
  const conductorTemplate = (rowData) => {
    return (
      <div style={{ textAlign: 'center' }}>
        <strong>{rowData.conductor}</strong>
        <><br />
          <small className="text-muted">{rowData.rut_conductor}</small>
        </>
      </div>
    );
  };

  // Vehículo
  const vehiculoTemplate = (rowData) => {
    return (
      <div style={{ textAlign: 'center' }}>
        <strong>{rowData.patente}</strong>
        <><br />
          <small className="text-muted">{rowData.tipo_vehiculo}</small>
        </>
      </div>
    );
  };

  // Medicación
  const medicacionTemplate = (rowData) => {
    return (
      <div style={{ textAlign: 'center' }}>
        <strong>{rowData.medicacion}</strong>
        {rowData.medicacion === 'Sí' && rowData.medicamento && (
          <> <br /> <Tag severity="warning" icon="pi pi-exclamation-triangle" value={rowData.medicamento}></Tag></>
        )}
      </div>
    );
  };

  // Helpers
  const asText = (v) => (v == null ? '' : String(v));
  
  const siNoPdf = (val, alertIf) => {
    const limpio = (val || '').toString().toLowerCase();
    return {
      text: val, // ya viene como "Sí" o "No"
      color: limpio === alertIf ? '#d32f2f' : 'black',
      bold: limpio === alertIf,
      alignment: 'center',
      margin: [0, 6, 4, 4]
    };
  };

  const formatMedicacionExport = (r) => {
    const esSi = (r.medicacion || '').toLowerCase() === 'sí';

    return {
      text: esSi && r.medicamento
        ? `Sí\n${r.medicamento}`
        : asText(r.medicacion),
      color: esSi ? '#d32f2f' : 'black',
      bold: esSi,
      margin: [0, 6, 4, 4]
    };
  };

  const formatVehiculoExport = (r) => {
    if (r.patente) { return `${r.patente}\n${r.tipo_vehiculo}`; } else { return ''; }
  };
  const formatConductorExport = (r) => {
    if (r.conductor) { return `${r.conductor}\n${r.rut_conductor}`; } else { return ''; }
  };

  // === Columnas para .xlsx ===
  const columnasExcel = [
    { header: 'CHK',                key: 'cod_checklist',     width: 18, get: r => asText(r['cod_checklist']) },
    { header: 'Fecha Registro',     key: 'creado_en',         width: 22, get: r => asText(r['creado_en']) },
    { header: 'Conductor',          key: 'conductor',         width: 22, get: r => asText(r['conductor']) },
    { header: 'RUT Conductor',      key: 'rut_conductor',     width: 14, get: r => asText(r['rut_conductor']) },
    { header: 'Patente',            key: 'patente',           width: 14, get: r => asText(r['patente']) },
    { header: 'Tipo Vehículo',      key: 'tipo_vehiculo',     width: 14, get: r => asText(r['tipo_vehiculo']) },
    { header: 'Durmió Bien',        key: 'durmioBien',        width: 14, get: r => asText(r['durmioBien']) },
    { header: 'Medicación',         key: 'medicacion',        width: 14, get: r => asText(r['medicacion']) },
    { header: 'Medicamento',        key: 'medicamento',       width: 14, get: r => asText(r['medicamento']) },
    { header: 'Actividad Exigente', key: 'actividadExigente', width: 14, get: r => asText(r['actividadExigente']) },
    { header: 'Resfriado',          key: 'resfriado',         width: 14, get: r => asText(r['resfriado']) },
    { header: 'Comida Copiosa',     key: 'comidasCopiosas',   width: 14, get: r => asText(r['comidasCopiosas']) },
    { header: 'Sintomas de Fatiga', key: 'cantSintomas',      width: 14, get: r => asText(r['cantSintomas']) }
  ];
  const columnasPDF = [
    { header: 'CHK',                get: r => ({ text: asText(r.cod_checklist), margin: [0, 6, 4, 4],}), width: 55 },
    { header: 'Fecha Registro',     get: r => ({ text: asText(r.creado_en), margin: [0, 6, 4, 4],}), width: 55 },
    { header: 'Conductor',          get: r => ({ text: formatConductorExport(r), margin: [0, 6, 4, 4],}), width: 88 },
    { header: 'Vehículo',           get: r => ({ text: formatVehiculoExport(r), margin: [0, 6, 4, 4],}), width: 49 },

    { header: 'Durmió Bien',        get: r => siNoPdf(r.durmioBien, 'no') }, // rojo si NO
    { header: 'Medicación',         get: r => ({ text: formatMedicacionExport(r), margin: [0, 6, 4, 4] }) },
    { header: 'Actividad Exigente', get: r => siNoPdf(r.actividadExigente, 'sí') }, // rojo si SI
    { header: 'Resfriado',          get: r => siNoPdf(r.resfriado, 'sí') },
    { header: 'Comida Copiosa',     get: r => siNoPdf(r.comidasCopiosas, 'sí') },
    { 
      header: 'Sintomas de Fatiga', 
      get: r => ({
        text: String(r.cantSintomas),
        color: 'black',
        fillColor: r.cantSintomas > 0 ? '#ffd1d1' : '#ffffff',
        bold: r.cantSintomas > 0,
        alignment: 'center',
        margin: [0, 6, 4, 4]
      })
    },
  ];

  // Toolbar 
  const startToolbar = () => {
    return(
      <div className='seleccionar-periodo'>
        <Permiso modulo="Panel Prevención" permiso="PRE_BUSCARPERIODO_SOM">
          <label className="font-bold">
            Seleccionar Periodo
          </label>
          <Calendar
            value={dates} 
            onChange={(e) => setDates(e.value)} 
            selectionMode="range" 
            readOnlyInput
            hideOnRangeSelection
            placeholder="Desde - Hasta"
            dateFormat="dd/mm/y" 
            showButtonBar
            className='input-text-m'
          />
          <Button 
            className="btn btn-primary"
            label= {labelBuscar}
            style={{ fontSize: fixSize, width: isPhone ? "50px" : "auto" }}
            icon="pi pi-search"
            onClick={() => buscarRegistros()}
          />
        </Permiso>
      </div>
    )
  };

  const endToolbar = () => {
    const nombreArchivo = 'resumen-checklist-somnolencia';
    const tituloArchivo = 'RESUMEN: CHECKLIST SOMNOLENCIA';
    const subTituloArchivo = 'Panel del Prevención';
    const rows  = filteredvalue.length > 0 ? filteredvalue : informes;

    return(
      <div className='tabla-toolbar-group'>
        <Permiso modulo="Panel Prevención" permiso="PRE_EXPORTAR_PDF">
          <Button 
            className="btn-pdf" 
            label={labelPdf}
            style={{ fontSize: fixSize, width: fixWidth }}
            icon="pi pi-file-pdf"
            onClick={() =>
              exportarPDF({ rows, columns: columnasPDF, nombreArchivo, tituloArchivo, subTituloArchivo })
            }
          />
        </Permiso>
        <Permiso modulo="Panel Prevención" permiso="PRE_EXPORTAR_EXCEL">
          <Button
            className="btn-excel"
            label={labelExcel}
            style={{ fontSize: fixSize, width: fixWidth}}
            icon="pi pi-file-excel"
            onClick={() =>
              exportarExcel({ rows, columns: columnasExcel, filename: `${nombreArchivo}.xlsx`, })
            }
          />
        </Permiso>
      </div>
    )
  };

  // Header
  const renderHeader = () => {
    const HeadingTag = isPhone ? "h5" : "h4";

    return (
      <div className="flex flex-wrap gap-2 justify-content-between align-items-center">
        <HeadingTag className="m-0">Resumen: Checklist Somnolencia</HeadingTag>
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search pr-2" />
          <InputText 
            value={globalFilterValue} 
            onChange={onGlobalFilterChange}
            className={`pl-5 ${isPhone ? "input-text-s" : ""}`}
            placeholder="Buscar..."
          />
        </IconField>
      </div>
    )
  };

  return (
    <TableLayout titulo="Histórico Checklist Somnolencia" logo={logo} onVolver={() => navigate('/app/panel-prevencion')}>
      <Toast ref={toast} position="center"/>
      <div className="table-container card">
        <Toolbar className="tabla-toolbar gap-2" 
          start={startToolbar} 
          end={endToolbar}>
        </Toolbar>

        <DataTable 
          ref={dt} value={informes} dataKey="CHK_ID"
          className="inv-data-table" size='small' 
          showGridlines selectionMode="single"
          header={renderHeader()} 
          // Barra de busqueda
          filters={filters}
          filteredvalue={filteredvalue.length > 0 ? filteredvalue : null}
          globalFilterFields={[ 
            'cod_checklist', 'conductor', 'patente', 'durmioBien', 'medicacion', 'actividadExigente', 
            'resfriado', 'comidasCopiosas', 'cantSintomas' 
          ]}
          filterDisplay="menu"

          // Paginador config
          paginator rows={10} rowsPerPageOptions={[10, 25, 50]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
          emptyMessage="No hay registros disponibles."
        >
          <Column field="cod_checklist"     header="CHK" style={{ minWidth: '100px'}} sortable/>
          <Column field="creado_en"         header="Fecha Registro" style={{ minWidth: '100px'}} sortable/>
          <Column header="Conductor"        body={conductorTemplate} sortable/>
          <Column header="Patente"          body={vehiculoTemplate} sortable/>
          
          <Column field="durmioBien"        header="Durmió Bien" sortable style={{textAlign: 'center'}}/>
          <Column header="Medicación"       body={medicacionTemplate} sortable style={{textAlign: 'center'}}/>
          <Column field="actividadExigente" header="Actividad Exigente" sortable style={{textAlign: 'center'}}/>
          <Column field="resfriado"         header="Resfriado" sortable style={{textAlign: 'center'}}/>
          <Column field="comidasCopiosas"   header="Comidas Copiosas" sortable style={{textAlign: 'center'}}/>
          <Column field="cantSintomas"      header="Síntomas de Fatiga" body={sintomasTemplate} sortable style={{textAlign: 'center'}}/>
          
          <Column header="Acción"           style={{textAlign: 'center', justifyContent: 'center'}} body={accionTemplate}/>
        </DataTable>
      </div>
    </TableLayout>
  );
}

export default HistChkSomnolencia;